import { translate } from '@teamleader/i18n';
import {
  IconWarningBadgedSmallFilled,
  IconChevronDownSmallOutline,
  IconChevronRightSmallOutline,
} from '@teamleader/ui-icons';
import cx from 'classnames';
import React, { useCallback, useContext, useState } from 'react';

import styles from './styles.css';
import useCurrencyFormatter from '../../../../../Components/Context/Hooks/useCurrencyFormatter';
import Box from '../../../../../Components/UI/Box';
import { MATCHED_STATUS } from '../../../../../Types/invoice';
import { apiFormatDate, formatDueDate, fromISO } from '../../../../../Utils/date';
import { InvoiceContext } from '../../../Context';

const InvoiceDetails: React.FC = () => {
  const { invoice, department } = useContext(InvoiceContext);
  const formatter = useCurrencyFormatter(invoice?.total.due.currency);
  const [isOpen, setIsOpen] = useState(true);
  const toggleMenu = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const Icon = isOpen ? IconChevronDownSmallOutline : IconChevronRightSmallOutline;

  if (!invoice || !department) {
    return null;
  }

  const { label, date, isOverDue } = formatDueDate(fromISO(invoice.dueDate), department.dateFormat);

  return (
    <Box className={styles['invoice-details']}>
      <Box className={styles['invoice-details-header']} onClick={toggleMenu}>
        <Icon />
        <h4>{translate('invoiceDetails')}</h4>
      </Box>
      {isOpen && (
        <Box className={styles['invoice-details-body']}>
          <Box className={styles['invoice-details-wrapper']}>
            {invoice.matchedStatus === MATCHED_STATUS.UNMATCHED && (
              <Box className={cx(styles['invoice-details-label-value'], styles['invoice-details-label-value-title'])}>
                <Box className={styles['invoice-details-label']}>{label}</Box>
                <Box className={styles['invoice-details-value']}>
                  {date} {isOverDue && <IconWarningBadgedSmallFilled className={styles['icon']} />}
                </Box>
              </Box>
            )}
            {invoice.matchedStatus !== MATCHED_STATUS.UNMATCHED && invoice.paidAt && (
              <Box className={cx(styles['invoice-details-label-value'], styles['invoice-details-label-value-title'])}>
                <Box className={styles['invoice-details-label']}>
                  {translate(invoice.matchedStatus === MATCHED_STATUS.FULLY_CREDITED ? 'creditedOn' : 'paidOn')}
                </Box>
                <Box className={styles['invoice-details-value']}>
                  {apiFormatDate(fromISO(invoice.paidAt), department.dateFormat)}
                </Box>
              </Box>
            )}
            {invoice.total.due.amount !== invoice.total.payable.amount && (
              <Box className={cx(styles['invoice-details-label-value'], styles['invoice-details-label-value--bold'])}>
                <Box className={styles['invoice-details-label']}>{translate('totalInvoiceAmount')}</Box>
                <Box className={styles['invoice-details-value']}>{formatter.format(invoice.total.payable.amount)}</Box>
              </Box>
            )}
            {invoice.total.interest.amount > 0 && (
              <Box className={styles['invoice-details-label-value']}>
                <Box className={styles['invoice-details-label']}>{translate('interest')}</Box>
                <Box className={styles['invoice-details-value']}>
                  + {formatter.format(invoice.total.interest.amount)}
                </Box>
              </Box>
            )}
            {invoice.total.fixedLateFee.amount > 0 && (
              <Box className={styles['invoice-details-label-value']}>
                <Box className={styles['invoice-details-label']}>{translate('fixedLateFee')}</Box>
                <Box className={styles['invoice-details-value']}>
                  + {formatter.format(invoice.total.fixedLateFee.amount)}
                </Box>
              </Box>
            )}
            {invoice.total.paidAmount.amount > 0 && (
              <Box className={styles['invoice-details-label-value']}>
                <Box className={styles['invoice-details-label']}>{translate('paid')}</Box>
                <Box className={styles['invoice-details-value']}>
                  - {formatter.format(invoice.total.paidAmount.amount)}
                </Box>
              </Box>
            )}
            {invoice.total.creditedAmount.amount > 0 && (
              <Box className={styles['invoice-details-label-value']}>
                <Box className={styles['invoice-details-label']}>{translate('credited')}</Box>
                <Box className={styles['invoice-details-value']}>
                  - {formatter.format(invoice.total.creditedAmount.amount)}
                </Box>
              </Box>
            )}
            <Box className={cx(styles['invoice-details-label-value'], styles['invoice-details-label-value--bold'])}>
              <Box className={styles['invoice-details-label']}>{translate('amountDue')}</Box>
              <Box className={styles['invoice-details-value']}>{formatter.format(invoice.total.due.amount)}</Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default InvoiceDetails;
